/** Layout principal: sidebar de navegacion + barra superior + contenido. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, Package, Users, PackagePlus,
  CreditCard, Boxes, Wallet, BarChart3, Settings, TrendingUp, Menu, LogOut,
  Receipt, UserCog, Tags, Landmark, ScanLine, X, ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { cerrarSesion } from '@/lib/authApi';
import { iniciales } from '@/lib/formato';
import { cn } from '@/lib/cn';
import { Brand } from './Brand';
import { TasaBadge } from './TasaBadge';
import { ToggleTema, BotonInstalarApp } from './NavbarAcciones';
import { useScannerGlobal } from '@/features/pos/useScannerGlobal';
import { useEscaneoRemoto } from '@/features/pos/useEscaneoRemoto';
import { useRealtime } from '@/app/useRealtime';

interface ItemNav {
  a: string;
  etiqueta: string;
  icono: typeof LayoutDashboard;
  permiso?: string;
  /** Solo se ofrece en teléfono: en la computadora no tiene sentido. */
  soloMovil?: boolean;
}

const NAV: { seccion: string; items: ItemNav[] }[] = [
  {
    seccion: 'Operación',
    items: [
      { a: '/', etiqueta: 'Resumen del negocio', icono: LayoutDashboard, permiso: 'dashboard.ver' },
      { a: '/pos', etiqueta: 'Punto de venta', icono: ShoppingCart, permiso: 'pos.vender' },
      // La cámara del teléfono como lector para la caja. En escritorio se oculta:
      // ahí ya está el lector de mano y la webcam no sirve para esto.
      { a: '/escanear', etiqueta: 'Escanear', icono: ScanLine, permiso: 'pos.vender', soloMovil: true },
      { a: '/ventas', etiqueta: 'Ventas', icono: Receipt, permiso: 'ventas.ver' },
      { a: '/tasas-cambio', etiqueta: 'Tasa del día', icono: TrendingUp, permiso: 'tasas.ver' },
    ],
  },
  {
    seccion: 'Catálogo',
    items: [
      { a: '/productos', etiqueta: 'Productos', icono: Package, permiso: 'productos.ver' },
      { a: '/clientes', etiqueta: 'Clientes', icono: Users, permiso: 'clientes.ver' },
    ],
  },
  {
    seccion: 'Inventario',
    items: [
      { a: '/compras', etiqueta: 'Entrada de mercancía', icono: PackagePlus, permiso: 'compras.ver' },
      { a: '/inventario', etiqueta: 'Existencias', icono: Boxes, permiso: 'inventario.ver' },
    ],
  },
  {
    seccion: 'Finanzas',
    items: [
      { a: '/creditos', etiqueta: 'Créditos', icono: CreditCard, permiso: 'creditos.ver' },
      { a: '/caja', etiqueta: 'Caja', icono: Wallet, permiso: 'caja.ver' },
      // Apartado aparte, sin relacion con las cifras de caja ni de reportes.
      { a: '/banco', etiqueta: 'Correlación con banco', icono: Landmark, permiso: 'caja.ver' },
      { a: '/reportes', etiqueta: 'Reportes', icono: BarChart3, permiso: 'reportes.ver' },
    ],
  },
  {
    seccion: 'Administración',
    items: [
      { a: '/categorias', etiqueta: 'Categorías', icono: Tags, permiso: 'categorias.ver' },
      { a: '/usuarios', etiqueta: 'Usuarios', icono: UserCog, permiso: 'usuarios.ver' },
      { a: '/configuracion', etiqueta: 'Configuración', icono: Settings, permiso: 'configuracion.ver' },
    ],
  },
];

export function AppLayout({ children }: { children: ReactNode }) {
  const [abierto, setAbierto] = useState(() => window.innerWidth >= 1024);
  const esMovil = () => window.innerWidth < 1024;
  const lateral = useRef<HTMLElement>(null);
  const botonMenu = useRef<HTMLButtonElement>(null);
  const { pathname } = useLocation();
  const actual = NAV.flatMap((g) => g.items.map((i) => ({ ...i, seccion: g.seccion }))).find((i) => i.a === pathname);
  const usuario = useAuthStore((s) => s.usuario);
  const permisos = useAuthStore((s) => s.permisos);
  const navegar = useNavigate();
  useScannerGlobal();
  useEscaneoRemoto();
  useRealtime();

  useEffect(() => {
    const ancho = window.matchMedia('(min-width: 1024px)');
    const cambiar = () => setAbierto(ancho.matches);
    ancho.addEventListener('change', cambiar);
    return () => ancho.removeEventListener('change', cambiar);
  }, []);

  useEffect(() => {
    if (!abierto || !esMovil()) return;
    const previo = botonMenu.current;
    lateral.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const teclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setAbierto(false); botonMenu.current?.focus(); }
      if (e.key !== 'Tab') return;
      const controles = lateral.current?.querySelectorAll<HTMLElement>('a[href], button');
      const visibles = Array.from(controles ?? []).filter((el) => el.getClientRects().length > 0);
      const primero = visibles[0]; const ultimo = visibles[visibles.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo?.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero?.focus(); }
    };
    document.addEventListener('keydown', teclado);
    return () => { document.removeEventListener('keydown', teclado); previo?.focus(); };
  }, [abierto]);

  const salir = async () => { await cerrarSesion(); navegar('/login'); };

  return (
    <div className="app-shell flex h-dvh overflow-hidden text-texto">
      <a href="#contenido-principal" className="skip-link">Ir al contenido</a>
      {abierto && <div onClick={() => setAbierto(false)} className="fixed inset-0 z-30 bg-black/40 lg:hidden" />}
      <aside ref={lateral} id="navegacion-principal" aria-label="Navegación principal" inert={!abierto}
        className={cn('app-sidebar fixed inset-y-0 left-0 z-40 flex w-[260px] shrink-0 flex-col transition-transform duration-150',
          abierto ? 'translate-x-0' : '-translate-x-full', 'lg:static lg:translate-x-0',
          abierto ? 'lg:w-[228px]' : 'lg:w-0 lg:overflow-hidden')}>
        <div className="flex min-h-[76px] items-center justify-between px-4">
          <Brand />
          <button onClick={() => setAbierto(false)} className="app-toolbar-button lg:hidden" aria-label="Cerrar menú"><X className="h-5 w-5" /></button>
        </div>
        <nav className="barra-fina flex-1 space-y-5 overflow-y-auto px-3 pb-4">
          {NAV.map((grupo) => {
            const visibles = grupo.items.filter((i) => !i.permiso || permisos.includes(i.permiso));
            if (!visibles.length) return null;
            return (
              <div key={grupo.seccion}>
                <p className="px-3 pb-2 text-xs text-texto-tenue">{grupo.seccion}</p>
                <div className="space-y-0.5">
                  {visibles.map((item) => (
                    <NavLink key={item.a} to={item.a} end={item.a === '/'}
                      onClick={() => { if (esMovil()) setAbierto(false); }}
                      className={({ isActive }) => cn('app-nav-link flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
                        item.soloMovil && 'md:hidden', isActive ? 'bg-primario-suave font-medium text-texto' : 'text-texto-suave hover:bg-primario-suave hover:text-texto')}>
                      <item.icono aria-hidden="true" />{item.etiqueta}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="mx-5 border-t border-borde py-4 text-xs text-texto-suave">
          <p className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--color-marca))]" />Control en USD y bolívares</p>
          <div className="mt-2 flex items-center justify-between sm:hidden"><BotonInstalarApp /><button onClick={salir} className="flex min-h-11 items-center gap-2 rounded-md px-2 text-sm"><LogOut className="h-4 w-4" />Cerrar sesión</button></div>
        </div>
      </aside>
      <div inert={abierto && esMovil()} className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex min-h-14 shrink-0 items-center justify-between gap-2 px-2 lg:min-h-12 lg:px-5">
          <div className="flex min-w-0 items-center gap-2">
            <button ref={botonMenu} onClick={() => setAbierto((v) => !v)} className="app-toolbar-button"
              aria-label={abierto ? 'Ocultar menú' : 'Abrir menú'} aria-expanded={abierto} aria-controls="navegacion-principal">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <span className="hidden text-texto-suave xl:inline">{actual?.seccion ?? 'Mi cuenta'}</span>
              <ChevronRight className="hidden h-3.5 w-3.5 text-texto-tenue xl:block" />
              {pathname === '/pos' ? <h1 className="truncate text-sm font-medium tracking-normal">Punto de venta</h1> : <span className="hidden truncate font-medium sm:block">{actual?.etiqueta ?? 'Mi cuenta'}</span>}
            </div>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-0.5 sm:gap-2">
            <TasaBadge /><ToggleTema /><div className="hidden sm:block"><BotonInstalarApp /></div>
            <Link to="/mi-cuenta" className="app-toolbar-button gap-2 px-1.5" title="Mi cuenta" aria-label="Mi cuenta">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-borde-fuerte bg-superficie text-xs font-medium text-texto">{iniciales(usuario?.nombreCompleto)}</div>
              <div className="hidden min-w-0 text-left leading-tight xl:block">
                <p className="max-w-40 truncate text-sm font-medium">{usuario?.nombreCompleto}</p>
                <p className="text-xs text-texto-suave">{usuario?.rolCodigo}</p>
              </div>
            </Link>
            <button onClick={salir} className="app-toolbar-button hidden sm:inline-flex" title="Cerrar sesión" aria-label="Cerrar sesión"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>
        {/* 1440 = 228 de menú + 8 de margen + 2 de borde + 48 de padding + 1154 de contenido. */}
        <main id="contenido-principal" tabIndex={-1} className="app-contenido app-sheet barra-fina min-h-0 min-w-0 flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
